/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";
import { ServiceOffering } from "~/models/services";
import { ROUTES } from "~/routing";
import {
  generateActionSuccessText,
  itHasNotOnlyWhitespace,
  useAxios,
} from "~/tools";
import { maxLength } from "~/tools/validation/validators";

type InputData = {
  name: string;
  serviceOfferingId: string;
};

type Props = {
  serviceOfferings: ServiceOffering[];
};

export default function ({ serviceOfferings }: Props) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const methods = useForm();
  const watcher = methods.watch();

  const history = useHistory();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: InputData) => {
    setLoading(true);
    try {
      const { data } = await Axios().post("/services", values);
      showSuccess(
        generateActionSuccessText(`Service ${values.name}`)()("added")()
      );
      history.push(`${ROUTES.services.index}/${data.id}`);
    } catch (e) {
      showError(e.response);
    }
    setLoading(false);
  };

  const offeringOptions = serviceOfferings.map((so) => ({
    label: so.name,
    value: so.id,
  }));

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginLeft: "auto" }}
        icon="plus"
      >
        Add new service
      </Button>

      <SidebarForm
        title="Add new service"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarDropdown
          name="serviceOfferingId"
          options={offeringOptions}
          label="Service offering"
          required
        />

        <SidebarTextField
          name="name"
          label="Name"
          required
          registerOptions={{
            ...maxLength(100),
            validate: itHasNotOnlyWhitespace,
          }}
        />

        <SidebarFormButtons
          submitText="Add"
          submitEnabled={!!(watcher.name && watcher.serviceOfferingId)}
          submitLoading={loading}
          onCancel={() => setIsSidebarOpen(false)}
        />
      </SidebarForm>
    </>
  );
}
